import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { Box } from '@mui/material';
import moment from 'moment';
import { toast } from 'react-toastify';

import { FHButton, FHSignatureDialog, FProgressBar } from '../../components/base';
import {
  clientActions,
  goalActions,
  parentSupervisionActions,
  providerAssignmentActions,
  treatmentPlanActions,
} from '../../store';
import { useParentSupervisionEditable } from '../../hooks';
import { history } from '../../helpers';
import { getUtcOffset } from '../../utils/common';
import { isCurrentParentSupervisionSave } from '../../utils/narrativeSuggestion';
import PageContainer from '../../components/layout/PageContainer';
import Controls from '../../components/layout/supervision-notes/parent/components/parentSupervisionControls';
import ParentProgress from '../../components/layout/supervision-notes/parent/ParentProgress';
import ParentGoalsProgress from '../../components/layout/supervision-notes/parent/ProgressV1/ParentGoalsProgress';
import ParentSessionNarrative from '../../components/layout/supervision-notes/parent/ParentSessionNarrative';
import { MINIMUM_REQUIRED_SENTENCE_LENGTH, SupervisionNoteStatus, Themes } from '../../constants';
import ParentTargetProgress from '../../components/layout/supervision-notes/parent/ProgressV2/ParentTargetProgress';
import ParentSNSignatureMessage from '../../components/layout/supervision-notes/parent/SignatureMessage';
import { PARENT_SN_VERSIONS } from '../../components/layout/supervision-notes/constants';

import type { ParentSupervisionUpdateHandler } from '../../components/layout/supervision-notes/types';
import type { RootState } from '../../store';
import type { IParentSupervision } from '../../utils/types';

function ParentSupervisionNote() {
  const { id: parentSupervisionId } = useParams();
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const isEditable = useParentSupervisionEditable();
  const { loading: providerAssignmentLoading } = useSelector(
    (state: RootState) => state.providerAssignment,
  );
  const {
    current: parentSupervision,
    loading: parentSNLoading,
    loadingTargetProgress,
  } = useSelector((state: RootState) => state.parentSupervision);
  const { loading: clientsLoading } = useSelector((state: RootState) => state.clients);

  const isSupervisionCompleted = parentSupervision?.status === SupervisionNoteStatus.Completed;

  const [openSignature, setOpenSignature] = useState<boolean>(false);
  const [updatedSupervision, setUpdatedSupervision] = useState<IParentSupervision | null>(null);
  const [signing, setSigning] = useState<boolean>(false);
  const [isParentsClearable, setIsParentsClearable] = useState<boolean>(false);
  const [showNarrativeError, setShowNarrativeError] = useState<boolean>(false);
  const currentNoteRef = useRef(parentSupervision);

  useEffect(() => {
    setShowNarrativeError(false);
  }, [updatedSupervision?.id]);

  useEffect(() => {
    return () => {
      dispatch(parentSupervisionActions.clearState());
      dispatch(clientActions.clearSelectedClient());
      dispatch(treatmentPlanActions.clearTreatmentPlan());
      dispatch(providerAssignmentActions.clearAssignment());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!parentSupervisionId || parentSupervisionId === 'new') {
      dispatch(parentSupervisionActions.clearState());
      return;
    }

    const fetchSupervisionNote = async () => {
      const { error } = await dispatch(
        parentSupervisionActions.getParentSupervision({
          parentSupervisionId,
          getAccessTokenSilently,
        }),
      );

      if (error) {
        history.navigate('/dashboard');
      }
    };

    fetchSupervisionNote();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentSupervisionId]);

  useEffect(() => {
    currentNoteRef.current = parentSupervision;
    setUpdatedSupervision(parentSupervision);
  }, [parentSupervision]);

  useEffect(() => {
    if (!parentSupervision?.date_start || !parentSupervision?.id) return;
    dispatch(
      goalActions.getGoals({
        treatmentPlanId: parentSupervision.active_treatment_plan_id,
        date: moment(parentSupervision?.date_start).utc().format(),
        getAccessTokenSilently,
      }),
    );
    return () => {
      dispatch(goalActions.clearState());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentSupervision?.date_start]);

  const handleUpdate: ParentSupervisionUpdateHandler = async (
    supervision,
    context,
    triggerApiCall = true,
  ) => {
    if (!supervision || supervision.status === SupervisionNoteStatus.Completed) return;
    setUpdatedSupervision(supervision);
    if (!supervision.id || !triggerApiCall) return;

    const data = {
      ...supervision,
      goals_and_targets_adhered: supervision?.goals_and_targets_adhered ?? false,
      client_id: supervision?.client?.id ?? null,
      parent_one_parent_and_caregiver_id:
        supervision.parent_one_parent_and_caregiver_id ??
        supervision.parent_one_parent_and_caregiver?.id ??
        null,
      parent_two_parent_and_caregiver_id:
        supervision.parent_two_parent_and_caregiver_id ??
        supervision.parent_two_parent_and_caregiver?.id ??
        null,
    };

    const res = await dispatch(
      parentSupervisionActions.updateParentSupervision({
        parentSupervisionId: supervision.id,
        data,
        context,
        getAccessTokenSilently,
      }),
    );

    if (
      !isCurrentParentSupervisionSave(currentNoteRef.current, supervision.id, context)
    ) {
      return Boolean(res && !res.error);
    }

    if (!res || res.error) {
      setUpdatedSupervision(currentNoteRef.current);
      setIsParentsClearable(true);

      if (res?.error?.code === '409' && supervision.id) {
        await dispatch(
          parentSupervisionActions.getParentSupervision({
            parentSupervisionId: supervision.id,
            getAccessTokenSilently,
          }),
        );
        return 'conflict';
      }

      return false;
    }

    setIsParentsClearable(false);
    return true;
  };

  // Validate at least one goal is added in DB
  const validateGoals = () => {
    if (!updatedSupervision) return;

    const { goal_progress = [] } = updatedSupervision;
    let isSaved = true;
    goal_progress.forEach((goal: any) => {
      if (!goal.id) isSaved = false;
    });
    return isSaved;
  };

  const validate = () => {
    setShowNarrativeError(false);
    if (!updatedSupervision) return;

    const {
      session_narrative,
      goal_progress,
      target_progress,
      parent_progress,
      parent_one_parent_and_caregiver,
      version,
    } = updatedSupervision;

    let targetToUpdate: any | null = null;
    target_progress?.forEach((progress) => {
      if (!targetToUpdate) {
        targetToUpdate = progress.target_evaluations.find((target) => target.total_attempts === 0);
      }
    });

    if (
      !(updatedSupervision.parent_one_parent_and_caregiver_id ??
        parent_one_parent_and_caregiver?.id)
    ) {
      toast.error('Parent 1 field is required');
      return;
    } else if (!parent_progress) {
      toast.error('Please add parent progress');
      return false;
    } else if (!goal_progress?.length && version === PARENT_SN_VERSIONS.V1) {
      toast.error('Please add at least one goal');
      return false;
    } else if (!target_progress?.length && version === PARENT_SN_VERSIONS.V2) {
      toast.error('Please add at least one target');
      return false;
    } else if (targetToUpdate) {
      toast.error(
        `All recorded targets must have been attempted at least once. Target ${targetToUpdate.target_label} has 0 total attempts`,
      );
      return false;
    } else if (!session_narrative || session_narrative.length < MINIMUM_REQUIRED_SENTENCE_LENGTH) {
      setShowNarrativeError(true);
      toast.error('Session narrative should have minimum 60 characters');
      return false;
    } else if (!validateGoals()) {
      toast.error(
        'Please ensure that progress is updated for all the goals that have been added in the Goal Progress section',
      );
      return false;
    }
    return true;
  };

  const handleConclude = () => {
    const isValid = validate();
    if (!isValid) {
      return;
    }
    setOpenSignature(true);
  };

  const handleSign = async (dataURL: string) => {
    if (!updatedSupervision || !dataURL) return;

    setSigning(true);

    const data = {
      signature: dataURL,
      date_signed: moment(new Date()),
      utc_offset_date_signed: getUtcOffset(moment(new Date())),
    };
    const response = await dispatch(
      parentSupervisionActions.stopParentSupervision({
        parentSupervisionId: updatedSupervision.id,
        data,
        getAccessTokenSilently,
      }),
    );

    setSigning(false);
    if (response.error) {
      return;
    }

    setOpenSignature(false);
    history.navigate('/dashboard');
  };

  const showV2ProgressUI = parentSupervision?.version === PARENT_SN_VERSIONS.V1 ? false : true;

  const dataLoading =
    clientsLoading || loadingTargetProgress || parentSNLoading || providerAssignmentLoading;

  return (
    <PageContainer
      dataTestId="parentSupervisionNotePageContainer"
      sx={{
        px: {
          xs: 2,
          sm: 3,
          md: 3,
          lg: 3,
          xl: 6,
        },
      }}
    >
      {dataLoading && <FProgressBar />}
      {parentSNLoading && <FProgressBar />}
      <Controls
        parentSNLoading={parentSNLoading}
        isSupervisionCompleted={isSupervisionCompleted}
        parentSupervision={updatedSupervision}
        isParentsClearable={isParentsClearable}
        onUpdate={handleUpdate}
      />
      <ParentProgress
        parentSupervision={updatedSupervision}
        onUpdate={handleUpdate}
        parentSNLoading={parentSNLoading}
      />
      {showV2ProgressUI ? (
        <ParentTargetProgress
          isSupervisionCompleted={isSupervisionCompleted}
          parentSNLoading={parentSNLoading}
          parentSupervision={updatedSupervision}
          loadingTargetProgress={loadingTargetProgress}
        />
      ) : (
        <ParentGoalsProgress parentSupervision={updatedSupervision} />
      )}
      <ParentSessionNarrative
        parentSupervision={updatedSupervision}
        showError={showNarrativeError}
        onUpdate={handleUpdate}
      />
      {isEditable && (
        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
          <FHButton
            data-testid="concludeparentSupervisionButton"
            sx={{ width: 200 }}
            onClick={handleConclude}
          >
            Conclude
          </FHButton>
        </Box>
      )}

      <FHSignatureDialog
        headerText="Signature"
        cancelText="Clear Signature"
        open={openSignature}
        signing={signing}
        onClose={() => setOpenSignature(false)}
        onSign={(dataURL: string) => handleSign(dataURL)}
        contentRenderer={ParentSNSignatureMessage}
        colorTheme={Themes.Primary}
      />
    </PageContainer>
  );
}

export default ParentSupervisionNote;
